import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { usePostsStore } from '../stores/postsStore';
import Post from '../components/Post';
import CreatePost from '../components/CreatePost';
import Friends from '../components/Friends';

const Dashboard = ({ viewingProfileId }) => {
  console.log('=== DASHBOARD COMPONENT RENDERING ===');

  const [searchParams] = useSearchParams();
  const { accessToken, profile } = useAuthStore();

  // Subscribe to store with explicit selector to ensure re-renders
  const posts = usePostsStore((state) => state.posts);
  const isLoading = usePostsStore((state) => state.isLoading);
  const hasMore = usePostsStore((state) => state.hasMore);
  const fetchPosts = usePostsStore((state) => state.fetchPosts);
  const selectedDate = usePostsStore((state) => state.selectedDate);
  const selectedPostId = usePostsStore((state) => state.selectedPostId);
  const resetToLatest = usePostsStore((state) => state.resetToLatest);
  const jumpToPost = usePostsStore((state) => state.jumpToPost);

  console.log('Dashboard store values:', { selectedDate, selectedPostId, postsCount: posts.length });

  const observerTarget = useRef(null);
  const [highlightedPost, setHighlightedPost] = useState(null);
  const [highlightedReply, setHighlightedReply] = useState(null);
  const [canPost, setCanPost] = useState(false);

  // Use viewingProfileId from props (the profile being viewed)
  const profileIdToShow = viewingProfileId || profile?.id;

  // Handle back to top
  const handleBackToTop = async () => {
    if (!accessToken || !profileIdToShow) return;
    await resetToLatest(accessToken, profileIdToShow);
  };

  // Debug logging
  console.log('Dashboard render:', { selectedDate, selectedPostId, showButton: !!(selectedDate || selectedPostId) });

  // Check if current user can post on this profile
  useEffect(() => {
    if (!accessToken || !profileIdToShow) return;

    const checkPostPermission = async () => {
      try {
        const response = await fetch(`/api/profile/can-post/${profileIdToShow}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        const data = await response.json();
        if (data.success) {
          setCanPost(data.can_post);
        }
      } catch (error) {
        console.error('Failed to check post permission:', error);
        setCanPost(false);
      }
    };

    checkPostPermission();
  }, [accessToken, profileIdToShow]);

  // Check URL params for post/reply highlighting
  useEffect(() => {
    const postParam = searchParams.get('post');
    const replyParam = searchParams.get('reply');

    if (postParam) {
      setHighlightedPost(parseInt(postParam));
      if (replyParam) {
        setHighlightedReply(parseInt(replyParam));
      }
    }
  }, [searchParams]);

  // Check URL params for jumpToPost (from notifications)
  useEffect(() => {
    const jumpToPostParam = searchParams.get('jumpToPost');
    const replyParam = searchParams.get('reply');

    console.log('Dashboard: Checking jumpToPost param:', {
      jumpToPostParam,
      replyParam,
      accessToken: !!accessToken,
      profileIdToShow,
      allParams: Array.from(searchParams.entries())
    });

    if (jumpToPostParam && accessToken && profileIdToShow) {
      console.log('Dashboard: jumpToPost param detected:', jumpToPostParam);
      const postId = parseInt(jumpToPostParam);
      const isReply = !!replyParam;

      // Call jumpToPost to load posts around this post
      jumpToPost(accessToken, postId, profileIdToShow, isReply);

      // Also set highlighted post
      setHighlightedPost(postId);
      if (replyParam) {
        setHighlightedReply(parseInt(replyParam));
      }
    }
  }, [searchParams, accessToken, profileIdToShow, jumpToPost]);

  // Fetch initial posts for the profile being viewed (only if not jumping to a post)
  useEffect(() => {
    const jumpToPostParam = searchParams.get('jumpToPost');

    console.log('Dashboard: Fetch initial posts check:', {
      accessToken: !!accessToken,
      profileIdToShow,
      jumpToPostParam,
      willFetch: !!(accessToken && profileIdToShow && !jumpToPostParam)
    });

    if (accessToken && profileIdToShow && !jumpToPostParam) {
      console.log('Dashboard: Fetching initial posts');
      fetchPosts(accessToken, true, profileIdToShow);
    }
  }, [accessToken, profileIdToShow, searchParams]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && profileIdToShow) {
          fetchPosts(accessToken, false, profileIdToShow);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, isLoading, accessToken, profileIdToShow]);

  // Sort posts: If there's a highlighted post, show it first
  const sortedPosts = [...posts].sort((a, b) => {
    if (highlightedPost) {
      if (a.id === highlightedPost) return -1;
      if (b.id === highlightedPost) return 1;
    }
    return 0; // Keep original order for others
  });

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Column - Posts Feed (2/3 width) */}
      <div className="flex-1 lg:w-2/3">
        {/* Back to Top Button - Show when date or post is selected */}
        {(selectedDate || selectedPostId) && (
          <div className="flex justify-center mb-4">
            <button
              onClick={handleBackToTop}
              className="w-10 h-10 rounded-full bg-gray-500 hover:bg-gray-600 text-white flex items-center justify-center transition-colors shadow-lg"
              title="Back to Latest"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Create Post - only show if user can post on this profile */}
        {canPost && <CreatePost postOnProfileId={profileIdToShow} />}

        {/* Posts Feed */}
        <div className="space-y-6">
          {posts.length === 0 && !isLoading && (
            <div className="surface rounded-lg shadow-lg p-8 text-center">
              <p className="text-secondary">No posts yet. Be the first to share something!</p>
            </div>
          )}

          {sortedPosts.map((post) => (
            <Post
              key={post.id}
              post={post}
              viewingProfileId={profileIdToShow}
              highlightedReply={post.id === highlightedPost ? highlightedReply : null}
            />
          ))}

          {/* Loading Indicator */}
          {isLoading && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
            </div>
          )}

          {/* Infinite Scroll Target */}
          <div ref={observerTarget} className="h-4" />
        </div>
      </div>

      {/* Right Column - Friends Sidebar (1/3 width) */}
      <div className="lg:w-1/3">
        <div className="sticky top-6">
          <Friends profileIdOverride={profileIdToShow} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
